import EChart from '@/components/EchartCustomized/MapEchart'
import { SLACompliance } from '@/modules/Ticket/types/response'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'

interface SLAComplianceChartProps {
    slaCompliance: SLACompliance | null
}

const SLAComplianceChart: React.FC<SLAComplianceChartProps> = ({
    slaCompliance,
}) => {
    const { t } = useTranslation()

    const total =
        (slaCompliance?.ontime_count || 0) +
        (slaCompliance?.late_count || 0) +
        (slaCompliance?.pending_count || 0)

    const chartData = [
        {
            value: slaCompliance?.ontime_count || 0,
            name: t('ticket-sla-ontime'),
            itemStyle: { color: '#4CAF50' },
        },
        {
            value: slaCompliance?.late_count || 0,
            name: t('ticket-sla-late'),
            itemStyle: { color: '#F44336' },
        },
        {
            value: slaCompliance?.pending_count || 0,
            name: t('ticket-sla-pending'),
            itemStyle: { color: '#FF9800' },
        },
    ]

    const ontimePercentage =
        total > 0
            ? Math.round(((slaCompliance?.ontime_count || 0) / total) * 100)
            : 0

    return (
        <div className="bg-white rounded-md p-4 lg:col-span-4 md:col-span-1 col-span-1 half-col-print">
            <Typogaphy size="small" className="text-primary font-semibold">
                {t('ticket-sla-compliance')} ({total})
            </Typogaphy>

            <div className="min-h-[280px] flex items-center justify-center">
                {!slaCompliance || total === 0 ? (
                    <Typogaphy className="text-gray-500 text-center text-sm">
                        {t('no-data')}
                    </Typogaphy>
                ) : (
                    <div className="flex-1 h-[280px] relative">
                        <EChart
                            options={{
                                tooltip: {
                                    trigger: 'item',
                                    formatter: '{b}: {c} ({d}%)',
                                },
                                legend: {
                                    show: false,
                                },
                                series: [
                                    {
                                        type: 'pie',
                                        radius: ['50%', '75%'],
                                        center: ['50%', '45%'],
                                        avoidLabelOverlap: false,
                                        itemStyle: {
                                            borderRadius: 4,
                                            borderColor: '#fff',
                                            borderWidth: 2,
                                        },
                                        label: {
                                            show: false,
                                        },
                                        emphasis: {
                                            label: {
                                                show: false,
                                            },
                                            scaleSize: 5,
                                        },
                                        data: chartData,
                                    },
                                ],
                                graphic: [
                                    {
                                        type: 'text',
                                        left: 'center',
                                        top: '38%',
                                        style: {
                                            text: `${ontimePercentage}%`,
                                            fontSize: 28,
                                            fontWeight: 'bold',
                                            fill:
                                                ontimePercentage >= 75
                                                    ? '#4CAF50'
                                                    : ontimePercentage >= 50
                                                      ? '#FF9800'
                                                      : '#F44336',
                                        },
                                    },
                                    {
                                        type: 'text',
                                        left: 'center',
                                        top: '50%',
                                        style: {
                                            text: t('ticket-sla-ontime'),
                                            fontSize: 11,
                                            fill: '#666',
                                        },
                                    },
                                ],
                            }}
                        />
                        {/* Legend at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-6 text-xs">
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-sm bg-[#4CAF50]" />
                                <span className="text-gray-600">
                                    {t('ticket-sla-ontime')}:{' '}
                                    <span className="font-semibold text-green-600">
                                        {slaCompliance.ontime_count}
                                    </span>
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-sm bg-[#F44336]" />
                                <span className="text-gray-600">
                                    {t('ticket-sla-late')}:{' '}
                                    <span className="font-semibold text-red-600">
                                        {slaCompliance.late_count}
                                    </span>
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-sm bg-[#FF9800]" />
                                <span className="text-gray-600">
                                    {t('ticket-sla-pending')}:{' '}
                                    <span className="font-semibold text-orange-500">
                                        {slaCompliance.pending_count}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SLAComplianceChart
